/* eslint-disable no-undef */
/* eslint-disable react/prop-types */
import React, { useState } from 'react';

import {
  ApartmentOutlined,
  DownOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Dropdown, Menu, Tooltip, Typography } from 'antd';

import styles from './HeaderActions.less';
import IframeModalView from '@/components/IframeModalView';

const { Text } = Typography;

/**
 * HeaderActions —— 顶部操作区
 * ------------------------------------------------------------------
 * 框架要求两项：
 *  1. Power 会话（原「LITTA POWER」）：hover 展开该商家**已绑定** Power 的教练列表
 *     （数据来自 /athena/coachDetail，按 `clawChannelBind.bound === true` 过滤）；
 *     点击后新标签页进入 palmer「Power 管理 · 会话」并定位到该成员对应的 Power 用户。
 *     未绑定的成员不再出现在下拉中。一个绑定都没有时按钮整体置灰并提示
 *     （判定与列表同一份数据）。
 *  2. 查看大人物：打开健身大人物二维码弹窗（弹窗内容由父级控制，
 *     复用既有 components/coachCode）
 *
 * 注：商家端数据中心通过 400×670 的 h5 iframe 打开，作为顶部快捷入口。
 *
 * 与「商家会话」的区别（勿混）：
 *   · 这里「Power 会话」= palmer Power 管理页（跨页定位 Power 用户）；
 *   · 商家成员详情卡里的「商家会话」= 老 litta-h5 客服会话深链（MemberTab）。
 *
 * @param coachList 商家教练列表（父级取 /athena/coachDetail，含 clawChannelBind{userId,bound}）
 * @param tenantInfo  用于商家端数据中心入口（tenantInfo.encryptCoachUserId）
 * @param tenantId    当前商家 tenantId（深链收窄「Power 会话」落地列表用）
 * @param onShowFitnessCoach 「查看大人物」回调
 */
const HeaderActions = (props) => {
  const {
    coachList = [],
    tenantInfo = {},
    tenantId,
    onShowFitnessCoach,
  } = props;
  const [dataCenterVisible, setDataCenterVisible] = useState(false);
  const [dataCenterUrl, setDataCenterUrl] = useState();

  const handlerDataCenter = () => {
    const coachUserId = tenantInfo.encryptCoachUserId;
    if (!coachUserId) return;

    setDataCenterUrl(
      UMI_MINI_H5HOST +
        '/litta-h5/#/datacenter?from=palmer&encryptCoachUserId=' +
        coachUserId,
    );
    setDataCenterVisible(true);
  };

  /**
   * 跳转 palmer「Power 管理 · 会话」并定位到该成员对应的 Power 用户。
   *
   * 数据契约（/athena/coachDetail → TenantCoachDto.clawChannelBind）：
   *   后端只回 `{ userId, bound }`，没有渠道明细（不存在 channels 字段，勿读）。
   *   userId 是商家成员业务 userId，也是商家端渠道的 Power accountId
   *   （accountId = litta_claw_channel_merchant.biz_user_id），所以深链固定走商家端。
   * 落地参数契约（与 ConversationPanel 顶部注释一致）：
   *   tab=conversations  channel=merchant  accountId=<userId>  tenantId=<商家>
   *   · tenantId 交给落地页按商家成员收窄用户列表（后端 requestData.tenantId），
   *     保证 accountId 一定落在首屏里，不会被全量用户挤出 200 条之外。
   * 局限：只在微信/企微有渠道记录、商家端无记录的教练，落地页定位不到，
   *       会回退到该商家首位用户并给出提示（后端未暴露按渠道明细，无法进一步区分）。
   */
  const powerSessionUrl = (coach) => {
    const query = new URLSearchParams();
    query.set('tab', 'conversations');
    query.set('channel', 'merchant');
    if (coach?.userId != null) query.set('accountId', String(coach.userId));
    // tenantId 用于落地页收窄；mock 预览态的 'mock-tenant' 等非数字值不发（后端要 Long）
    if (/^\d+$/.test(String(tenantId ?? ''))) {
      query.set('tenantId', String(tenantId));
    }
    // 同源相对路径：目标与当前页同为 palmer 后台 SPA（admin 域），用相对路径可自动沿用
    // 当前 origin 与登录态；不要用 UMI_PALMER（它指向 h5 域），否则跨源丢 token 会被
    // layouts 登录守卫踢到 /login?timestamp=...。
    return `/litta-palmer/technical/powerManager?${query.toString()}`;
  };

  /** 绑定口径：clawChannelBind.bound === true 才可点击 */
  const isBound = (coach) => coach?.clawChannelBind?.bound === true;

  /* 只展示已绑定（可进入 Power 会话）的成员：/athena/coachDetail 返回的是商家全量成员，
     未绑定的成员不进入下拉，避免噪音；顺序保持接口返回顺序。 */
  const boundCoaches = coachList.filter(isBound);

  const droplist = (
    <Menu className={styles.coachMenu}>
      <Menu.ItemGroup
        title={`教练 ${boundCoaches.length} 位 · 点击进入 Power 会话`}
      >
        {boundCoaches.map((coach, index) => {
          const name = coach.coachNickName || coach.coachText || '未命名教练';
          return (
            <Menu.Item key={coach.coachId || coach.encryptUserId || index}>
              <a
                className={styles.coachItem}
                href={powerSessionUrl(coach)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Avatar size={28} className={styles.avatar}>
                  {name.slice(0, 1)}
                </Avatar>
                <span className={styles.coachMain}>
                  <span className={styles.coachName}>{name}</span>
                  {coach.coachText && (
                    <Text className={styles.coachSub} type="secondary">
                      {coach.coachText}
                    </Text>
                  )}
                </span>
              </a>
            </Menu.Item>
          );
        })}
      </Menu.ItemGroup>
    </Menu>
  );

  /* 按钮置灰判定：与列表同一份数据——只要有一个教练已绑定（bound=true），
     就说明该商家用过 Power；全部未绑定则整体置灰（不再单独请求 powerBindStatus）。 */
  const hasPower = boundCoaches.length > 0;

  return (
    <div className={styles.wrap}>
      {hasPower ? (
        <Dropdown
          overlay={droplist}
          placement="bottomRight"
          trigger={['hover']}
        >
          <Button
            type="primary"
            icon={<ApartmentOutlined />}
            className={styles.btn}
          >
            Power 会话
            <DownOutlined className={styles.caret} />
          </Button>
        </Dropdown>
      ) : (
        /* antd 4 的 Tooltip 包在 disabled Button 上不触发（按钮不派发鼠标事件），
           需在外层套一个可 hover 的元素，否则提示永远弹不出来。 */
        <Tooltip title="该商家暂无成员使用过 Power">
          <span className={styles.disabledWrap}>
            <Button
              type="primary"
              icon={<ApartmentOutlined />}
              className={styles.btn}
              disabled
            >
              Power 会话
            </Button>
          </span>
        </Tooltip>
      )}
      {tenantInfo?.encryptCoachUserId && (
        <Button type="primary" onClick={handlerDataCenter}>
          商家端数据中心
        </Button>
      )}
      <Button
        type="default"
        icon={<QrcodeOutlined />}
        onClick={onShowFitnessCoach}
        className={styles.btn}
      >
        查看大人物
      </Button>
      <IframeModalView
        title={'数据中心'}
        src={dataCenterUrl}
        visible={dataCenterVisible}
        setVisible={setDataCenterVisible}
        showBack={true}
      />
    </div>
  );
};

export default HeaderActions;
